import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../hooks/apiRequest";
import { ProjectProps } from "../types/interfaces";
import { projectKeys } from "../utils/queryKeys";

interface ProjectFilters {
  clientId?: string;
  enabled?: boolean;
}

function toQueryString(filters: ProjectFilters): string {
  const params = new URLSearchParams();
  if (filters.clientId) params.set("clientId", filters.clientId);
  return params.toString();
}

// `filters` is optional and defaults to `{}` so every pre-existing call site
// (TimesheetPage, TicketListPage, ProjectListPage, ReportsPage) keeps
// fetching the caller's full scoped project list, unfiltered and always
// enabled. Only when a caller explicitly passes a `clientId` key (even as
// `undefined`, e.g. TicketNewPage before a client is chosen) does the query
// wait for a truthy value before firing — see the `"clientId" in filters`
// check below. `enabled` is a separate opt-out a caller can pass regardless
// of `clientId` (e.g. UserListPage gating the assigned-projects multi-select
// data on the viewer being an admin) — it's ANDed with the clientId-presence
// check so either one can suppress the query.
export function useProjects(filters: ProjectFilters = {}) {
  const queryString = toQueryString(filters);
  return useQuery({
    queryKey: projectKeys.list(filters.clientId),
    queryFn: async () =>
      (await apiRequest<ProjectProps[]>(`projects${queryString ? `?${queryString}` : ""}`)).data,
    enabled: (filters.enabled ?? true) && ("clientId" in filters ? !!filters.clientId : true),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: async () => (await apiRequest<ProjectProps>(`projects/${id}`)).data,
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { clientId: string; name: string; description?: string; guideUrl?: string }) =>
      (await apiRequest<ProjectProps>("projects", { method: "POST", body: JSON.stringify(data) })).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.list(variables.clientId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.list(undefined) });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Pick<ProjectProps, "name" | "description" | "status" | "guideUrl">> }) =>
      (await apiRequest<ProjectProps>(`projects/${id}`, { method: "PUT", body: JSON.stringify(data) })).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.id) });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiRequest<void>(`projects/${id}`, { method: "DELETE" })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: projectKeys.all }),
  });
}
